import _ from 'lodash';
import { FEATURES, SCREEN_OPTIONS } from './constants';
import appInfoService from '../_helpers/appInfoService';
import { store } from '../..';

export const getActiveMissingFeatures = (missingFeatures, theme) => {
    if (_.isEmpty(missingFeatures)) {
        return {};
    }

    appInfoService.init();
    let activeMissingFeatures = { android: [], ios: [] };

    // For each feature and platform on the missingFeatures array, check 
    // if it is active on this theme/store or not.
    for (let platform in missingFeatures) {
        missingFeatures[platform].forEach(missingFeature => {
            // We need to check for a missing feature only if the app is
            // published on the corresponding platform
            if (appInfoService.isAppPublished(platform)) {
                if (missingFeature === FEATURES.BUY_BUTTON_CARD) {
                    const productCardsSettings = theme.settings.find(s => s.key === 'productCardsSettings');
                    if (productCardsSettings && productCardsSettings.options?.showBuyButton) {
                        activeMissingFeatures[platform].push(FEATURES.BUY_BUTTON_CARD);
                    }
                }

                if (missingFeature === FEATURES.WISHLIST) {
                    const wishlistSettings = theme.settings.find(s => s.key === 'wishlist');
                    if (wishlistSettings && wishlistSettings.showWishlist) {
                        activeMissingFeatures[platform].push(FEATURES.WISHLIST);
                    }
                }

                if (missingFeature === FEATURES.VIDEO_SECTION) {
                    const homeArea = theme.areas.find(a => a.key === 'homeScreen');
                    const videoSection = _.get(homeArea, 'sections', []).find(s => s.key === 'video');
                    if (videoSection && videoSection.isVisible) {
                        activeMissingFeatures[platform].push(FEATURES.VIDEO_SECTION);
                    }
                }

                if (missingFeature === FEATURES.PROMOTIONAL_BANNER) {
                    const promotionalBannerSettings = theme.settings.find(s => s.key === 'promotionalBanner');
                    if (promotionalBannerSettings && promotionalBannerSettings.options?.isVisible) {
                        activeMissingFeatures[platform].push(FEATURES.PROMOTIONAL_BANNER);
                    }
                }

                if (missingFeature === FEATURES.CATEGORY_TITLE_COLOR_CHANGE) {
                    const categoryScreenArea = theme.areas.find(a => a.key === 'categoryScreen');
                    const categorySettings = _.get(categoryScreenArea, 'sections', []).find(s => s.key === 'categorySettings');

                    let titleBackgroundColor = _.get(categorySettings, 'options.titleBackgroundColor', '#000000');
                    let titleFontColor = _.get(categorySettings, 'options.titleFontColor', '#ffffff');

                    if (titleBackgroundColor !== '#000000' || titleFontColor !== '#ffffff') {
                        activeMissingFeatures[platform].push(FEATURES.CATEGORY_TITLE_COLOR_CHANGE);
                    }
                }

                if (missingFeature === FEATURES.SHIPPING_CALCULATOR) {
                    const productDetailsArea = theme.areas.find(a => a.key === 'productDetailsScreen');
                    const productDetailsSettings = _.get(productDetailsArea, 'sections', []).find(s => s.key === 'productDetailsSettings');
                    const productOptions = _.get(productDetailsSettings, 'options', {});

                    const cartScreenArea = theme.areas.find(a => a.key === 'cartScreen');
                    const cartScreenSettings = _.get(cartScreenArea, 'sections', []).find(s => s.key === 'cartScreenSettings');
                    const cartOptions = _.get(cartScreenSettings, 'options', {});

                    if (productOptions?.showShipping || cartOptions?.showShipping) {
                        activeMissingFeatures[platform].push(FEATURES.SHIPPING_CALCULATOR);
                    }
                }

                if (missingFeature === FEATURES.STATUS_BAR_FONT_COLOR) {
                    const headerSettings = theme.settings.find(s => s.key === 'header');
                    const headerOptions = _.get(headerSettings, 'options', {});

                    if (headerOptions.statusBarTextColor != '#fff') {
                        activeMissingFeatures[platform].push(FEATURES.STATUS_BAR_FONT_COLOR);
                    }
                }

                if (missingFeature === FEATURES.HEADER_VISIBLE) {
                    const headerSettings = theme.settings.find(s => s.key === 'header');
                    const headerOptions = _.get(headerSettings, 'options', {});

                    if (headerOptions.isVisible == false) {
                        activeMissingFeatures[platform].push(FEATURES.HEADER_VISIBLE);
                    }
                }

                if (missingFeature === FEATURES.MENU_ITEMS) {
                    const drawer = theme.settings.find(s => s.key === 'drawerMenu');
                    const drawerContent = _.get(drawer, 'content', []);
                    const tabBar = theme.settings.find(s => s.key === 'tabBar');
                    const tabBarContent = _.get(tabBar, 'content', []);

                    let screenChanged = drawerContent.length != 6 || tabBarContent.length != 6 ? true : false;

                    const actionChanged = drawerContent.find(x => x.action != 'goToScreen')
                        || tabBarContent.find(x => x.action != 'goToScreen')

                    for (const item of drawerContent) {
                        const defaultScreen = SCREEN_OPTIONS.find(x => x.key == item.key);
                        const defaultScreenKeys = Object.keys(defaultScreen);

                        for (const key of defaultScreenKeys) {
                            if (item[key] != defaultScreen[key] && key != 'relatedScreens') {
                                screenChanged = true
                            }
                        }
                    }

                    for (const item of tabBarContent) {
                        const defaultScreen = SCREEN_OPTIONS.find(x => x.key == item.key);
                        const defaultScreenKeys = defaultScreen ? Object.keys(defaultScreen) : null

                        if (defaultScreenKeys) {
                            for (const key of defaultScreenKeys) {
                                if (item[key] != defaultScreen[key] && key != 'relatedScreens') {
                                    screenChanged = true
                                }
                            }
                        }
                    }

                    if (actionChanged || screenChanged) {
                        activeMissingFeatures[platform].push(FEATURES.MENU_ITEMS);
                    }
                }

                if (missingFeature === FEATURES.SEARCH_TERM_ACTION) {
                    const containsGoToSearch = obj =>
                        obj && (Array.isArray(obj)
                            ? obj.some(containsGoToSearch)
                            : typeof obj === 'object'
                                ? Object.entries(obj).some(([k, v]) => k === 'action' && v === 'goToSearch' || containsGoToSearch(v))
                                : false
                        );

                    if (containsGoToSearch(theme)) {
                        activeMissingFeatures[platform].push(FEATURES.SEARCH_TERM_ACTION);
                    }
                }

                if (missingFeature === FEATURES.PRODUCT_SEARCH_TERM_ACTION) {
                    const containsGoToSearch = obj =>
                        obj && (Array.isArray(obj)
                            ? obj.some(containsGoToSearch)
                            : typeof obj === 'object'
                                ? Object.entries(obj).some(([k, v]) => k === 'productsOrigin' && v === 'search' || containsGoToSearch(v))
                                : false
                        );

                    if (containsGoToSearch(theme)) {
                        activeMissingFeatures[platform].push(FEATURES.PRODUCT_SEARCH_TERM_ACTION);
                    }
                }

                if (missingFeature === FEATURES.MENU_SEARCH_TERM_ACTION) {
                    const drawer = theme.settings.find(s => s.key === 'drawerMenu');
                    const drawerContent = _.get(drawer, 'content', []);

                    const actionChanged = drawerContent.find(x => x.action === 'goToSearch')

                    if (actionChanged) {
                        activeMissingFeatures[platform].push(FEATURES.MENU_SEARCH_TERM_ACTION);
                    }
                }

                if (missingFeature === FEATURES.SHOW_PRODUCT_AVAILABILITY) {
                    const productDetailsArea = theme.areas.find(a => a.key === 'productDetailsScreen');
                    const productDetailsSettings = _.get(productDetailsArea, 'sections', []).find(s => s.key === 'productDetailsSettings');
                    const productOptions = _.get(productDetailsSettings, 'options', {});

                    if (productOptions?.showAvailability) {
                        activeMissingFeatures[platform].push(FEATURES.SHOW_PRODUCT_AVAILABILITY);
                    }
                }

                if (missingFeature === FEATURES.SHOW_NAVIGATION_TAB_BAR) {
                    const settings = store.getState().UserReducer.user.store;
                    const iOSPublishStatus = _.get(settings, 'platforms.ios.publishStatus', 'first_publish_pending');
                    const tabBar = theme.settings.find(s => s.key === 'tabBar');
                    const tabBarOptions = _.get(tabBar, 'options', []);

                    if (tabBarOptions.showNavigation == true && iOSPublishStatus != 'first_publish_pending') {
                        activeMissingFeatures[platform].push(FEATURES.SHOW_NAVIGATION_TAB_BAR);
                    }
                }

                if (missingFeature === FEATURES.WEBVIEW_ACTION_HOME_COMPONENTS) {
                    const hasWebviewAction = section => (
                        _.get(section, 'options.action') === 'goToWebview' ||
                        _.get(section, 'content', []).some(item => item.action === 'goToWebview')
                    );

                    const areasSections = _.get(theme, 'areas', []).flatMap(a => _.get(a, 'sections', []));
                    const promotionalBanner = theme.settings.find(s => s.key === 'promotionalBanner');

                    if (areasSections.some(hasWebviewAction) || hasWebviewAction(promotionalBanner)) {
                        activeMissingFeatures[platform].push(FEATURES.WEBVIEW_ACTION_HOME_COMPONENTS);
                    }
                }

                if (missingFeature === FEATURES.NOTIFICATION_CENTER) {
                    const header = theme.settings.find(s => s.key === 'header');
                    const headerOptions = _.get(header, 'options', []);
                    const drawer = theme.settings.find(s => s.key === 'drawerMenu');
                    const drawerContent = _.get(drawer, 'content', []);
                    const tabBar = theme.settings.find(s => s.key === 'tabBar');
                    const tabBarContent = _.get(tabBar, 'content', []);

                    const showNotificationHeader = headerOptions.showNotificationIcon === true ? true : false
                    const showNotificationDrawer = drawerContent.find(x => x.link === 'NotificationStack')
                    const showNotificationTabBar = tabBarContent.find(x => x.link === 'NotificationStack')

                    if (showNotificationHeader || showNotificationDrawer || showNotificationTabBar) {
                        activeMissingFeatures[platform].push(FEATURES.NOTIFICATION_CENTER);
                    }
                }

                if (missingFeature === FEATURES.TEXT_ALIGN_PRODUCT_SCREEN) {
                    const productDetailsArea = theme.areas.find(a => a.key === 'productDetailsScreen');
                    const productDetailsSettings = _.get(productDetailsArea, 'sections', []).find(s => s.key === 'productDetailsSettings');
                    const productOptions = _.get(productDetailsSettings, 'options', {});
                    if (productOptions?.textAlign && productOptions.textAlign !== 'left') {
                        activeMissingFeatures[platform].push(FEATURES.TEXT_ALIGN_PRODUCT_SCREEN);
                    }
                }
            }
        });
    }

    // Remove empty properties from activeMissingFeatures object
    activeMissingFeatures = _.omitBy(activeMissingFeatures, _.isEmpty);

    return activeMissingFeatures;
}